/* eslint-disable max-len */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useUserData } from 'hooks/UserContext';
import { useSearch } from 'hooks/SearchContext';
import Services from 'services/Services';
import MiniCard from 'components/MiniCard/MiniCard';
import PrettyContactModal from 'components/PrettyContactModal';
import CannotPostulateModal from 'components/ContactModal/CannotPostulateModal';
import { Helmet } from 'react-helmet';
import Header from 'components/Header/Header';
import NotLoggedModal from 'components/NotLogged';
import countryService from 'services/countryService';
import SliderGallery from 'components/SliderGallery';
import AppComponents from '../../constants/components';
import { SchoolProvider, useSchool } from './SchoolContext';
import { getSchoolInfo } from './utils';
import AsideScroll from './AsideScroll/AsideScroll';
import UpperLeftActions from './ProfileSection/UpperLeftActions/upperLeftActions';
import styles from './styles.module.css';
import Description from './ProfileSection/Description/Description';
import MainFeatures from './ProfileSection/MainFeatures/MainFeatures';
import ExtracurricularActivities from './ProfileSection/ExtracurricularActivities/ExtracurricularActivities';
import SecuritiesMeasures from './ProfileSection/SecuritiesMeasures/SecuritiesMeasures';
import InstalacionsActivities from './ProfileSection/InstalacionsActivities/InstalacionsActivities';
import EventsActivities from './ProfileSection/EventsActivities/EventsActivities';
import AwardsRecognitions from './ProfileSection/AwardsRecognitions/AwardsRecognitions';
import CurricularAdaptation from './ProfileSection/CurricularAdaptation/CurricularAdaptation';
import IncludedServices from './ProfileSection/IncludedServices/IncludedServices';
import EducationalLevels from './ProfileSection/EducationalLevels/EducationalLevels';
import Languages from './ProfileSection/Languages/Languages';
import Religion from './ProfileSection/Religion';
import GoogleReviews from './ProfileSection/GoogleReviews/GoogleReviews';
import Footer from '../../components/Footer';
import SchoolProfileMap from './ProfileSection/Map/SchoolProfileMap';
import asideSessions from './schoolProfileConstants';
import ExperienceButton from '../Experience/src/components/button';
import AddRemoveCandidateButton from '../Experience/src/components/buttons/add-remove-candidate';

const SchoolProfileContent = ({ id }) => {
  const bottomBar = useMediaQuery({ query: '(max-width: 740px)' });
  const location = useLocation();
  const { setUsedCoordinates } = useSearch();
  const { userData, activeExperience } = useUserData();
  const { loginStatus } = userData;
  const { schoolData, isLoading, error } = useSchool();

  const currentCountryId = countryService.getCurrentCountryId();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [schoolCanBeContacted, setSchoolCanBeContacted] = useState(false);
  const [notLoggedMsg, setNotLoggedMsg] = useState('');
  const [notLoggedAction, setNotLoggedAction] = useState('');
  const [isCannotPostulateModalOpen, setIsCannotPostulateModalOpen] = useState(false);
  const [selectedModalAction, setSelectedModalAction] = useState('');
  const [componentFrom, setComponentFrom] = useState();

  // add ref to the sections
  const basicInfoRef = useRef(null);
  const mainFeaturesRef = useRef(null);
  const educationalLevelsRef = useRef(null);
  const religionRef = useRef(null);
  const languagesRef = useRef(null);
  const mapRef = useRef(null);
  const reviewRef = useRef(null);
  const extracurricularActivitiesRef = useRef(null);
  const curricularAdaptationRef = useRef(null);
  const includedServicesRef = useRef(null);
  const securitiesMeasuresRef = useRef(null);
  const instalacionsActivitiesRef = useRef(null);
  const eventsActivitiesRef = useRef(null);
  const awardsRecognitionsRef = useRef(null);

  const auxSections = asideSessions;
  const basicInfoSection = auxSections.find((s) => /inform/i.test(s.name));
  const mainFeaturesSection = auxSections.find((s) => /caracter/i.test(s.name));
  const religionSection = auxSections.find((s) => /Relig/i.test(s.name));
  const languageSection = auxSections.find((s) => /idiomas/i.test(s.name));
  const extracurricularActivitiesSection = auxSections.find((s) => /xtracurriculares/i.test(s.name));
  const curricularAdapationSection = auxSections.find((s) => /adaptación/i.test(s.name));
  const includedServicesSection = auxSections.find((s) => /servicios/i.test(s.name));
  const mapSection = auxSections.find((s) => /ubicac/i.test(s.name));
  const reviewSection = auxSections.find((s) => /eseñas/i.test(s.name));
  const securitiesMeasures = auxSections.find((s) => /seguridad/i.test(s.name));
  const instalacionsActivities = auxSections.find((s) => /instalaciones/i.test(s.name));
  const eventsActivities = auxSections.find((s) => /eventos/i.test(s.name));
  const awardsRecognitions = auxSections.find((s) => /reconocimientos/i.test(s.name));
  const educationalLevels = auxSections.find((s) => /propuesta/i.test(s.name));

  basicInfoSection.ref = basicInfoRef;
  mainFeaturesSection.ref = mainFeaturesRef;
  religionSection.ref = religionRef;
  languageSection.ref = languagesRef;
  extracurricularActivitiesSection.ref = extracurricularActivitiesRef;
  curricularAdapationSection.ref = curricularAdaptationRef;
  includedServicesSection.ref = includedServicesRef;
  mapSection.ref = mapRef;
  reviewSection.ref = reviewRef;
  securitiesMeasures.ref = securitiesMeasuresRef;
  instalacionsActivities.ref = instalacionsActivitiesRef;
  eventsActivities.ref = eventsActivitiesRef;
  awardsRecognitions.ref = awardsRecognitionsRef;
  educationalLevels.ref = educationalLevelsRef;

  const handleAction = (action) => {
    setSelectedModalAction(action);
    setIsContactModalOpen(true);
  };

  React.useEffect(() => {
    if (schoolData?.id === Number(id)) {
      const schoolIsActive = !!schoolData.active;
      const schoolContactDisabled = !!schoolData.contact_disabled;
      const schoolIsNotPublic = !!schoolData?.public && schoolData.public.is_public === false;
      const schoolCanBeContactedResult = schoolIsNotPublic
                && !schoolContactDisabled
                && schoolIsActive;
      setSchoolCanBeContacted(schoolCanBeContactedResult);

      const searchParams = new URLSearchParams(location.search);

      const fromParam = searchParams.get('from');
      if (fromParam) {
        const from = AppComponents.findByName(fromParam);
        if (from) {
          setComponentFrom(from);
        }
      }

      if (schoolCanBeContactedResult) {
        const action = searchParams.get('action');
        if (action) {
          handleAction(action);
        }
      }

      window.history.replaceState(null, '', `${window.location.pathname}`);
      location.search = '';
    }
  }, [schoolData?.id]);

  React.useEffect(() => {
    if (schoolData?.city?.latitude && schoolData?.city?.longitude) {
      setUsedCoordinates({
        latitude: schoolData.city.latitude,
        longitude: schoolData.city.longitude,
      });
    }
  }, [schoolData?.city]);

  React.useEffect(() => {
    const schoolInfo = getSchoolInfo(schoolData || {});
    if (schoolInfo.schoolAffinity) {
      setUsedCoordinates(schoolInfo.schoolAffinity.location);
    }
  }, [schoolData]);

  const scrollToRef = (ref) => {
    if (ref) window.scrollTo({ top: ref.current.offsetTop, behavior: 'smooth' });
  };

  function handleCloseContactModal() {
    setIsContactModalOpen(false);
  }

  async function handleCloseCannotPostulateModal(check) {
    if (loginStatus && !schoolData?.postulate) {
      const alert = check;
      const postulationAlert = {
        school_id: id,
        postulated: false,
        alert,
      };

      await Services.savePostulationAlert(postulationAlert);
    }

    setIsCannotPostulateModalOpen(false);
  }

  if (isLoading) {
    return <>Loading...</>;
  }

  if (error) {
    return <div>Error!</div>;
  }

  const renderGallery = () => {
    if (!schoolData?.gallery_pictures?.length) {
      return <div className={styles.empty__gallery} />;
    }

    return (
      <div className={styles.gallery__container}>
        <SliderGallery
          // flkty={flkty}
          galleryImages={schoolData.gallery_pictures.map((picture) => ({
            url: picture.url,
            name: picture.name,
          }))}
          isFetching={isLoading}
        />
      </div>
    );
  };

  const renderGoogleReviews = () => {
    if (schoolData?.public?.is_public) return <></>;

    return (
      <div ref={reviewRef}>
        <GoogleReviews isFetching={isLoading} />
      </div>
    );
  };

  return (
    currentCountryId !== schoolData.country_id ? <p>School from another country</p> : (
      <div className={styles['footer-container']}>
        <Header />
        <Helmet>
          {schoolData?.name ? <title>{`${schoolData?.name} - VacantED`}</title> : null}
          {schoolData?.name ? <meta name="description" content={`Ficha institucional de ${schoolData?.name}`} /> : null}
        </Helmet>
        {renderGallery()}
        <div className={styles.container}>
          <div className={styles.pageWrapper}>
            <MiniCard
              isFetching={isLoading}
              positioning={bottomBar ? null : 0}
            />
            <div className={styles.mainWrapper}>
              <main className={styles.main}>
                <div>
                  {!isLoading
                                        && (
                                        <UpperLeftActions
                                          website={schoolData?.website}
                                          schoolObj={schoolData}
                                          setMsg={setNotLoggedMsg}
                                          setNotLoggedAction={setNotLoggedAction}
                                        />
                                        )}
                </div>
                <div ref={basicInfoRef}>
                  <Description isFetching={isLoading} />
                </div>
                <div ref={mainFeaturesRef}>
                  <MainFeatures isFetching={isLoading} sections={auxSections} />
                </div>
                <div ref={educationalLevelsRef}>
                  <EducationalLevels isFetching={isLoading} />
                </div>
                <div ref={languagesRef}>
                  <Languages isFetching={isLoading} />
                </div>
                <div ref={religionRef}>
                  <Religion
                    religionDescription={schoolData?.religious_description}
                    religionName={schoolData?.religion?.name}
                    isFetching={isLoading}
                  />
                </div>
                <div ref={extracurricularActivitiesRef}>
                  <ExtracurricularActivities isFetching={isLoading} />
                </div>
                <div ref={curricularAdaptationRef}>
                  <CurricularAdaptation isFetching={isLoading} />
                </div>
                <div ref={includedServicesRef}>
                  <IncludedServices isFetching={isLoading} />
                </div>
                <div ref={instalacionsActivitiesRef}>
                  <InstalacionsActivities isFetching={isLoading} />
                </div>
                <div ref={awardsRecognitionsRef}>
                  <AwardsRecognitions isFetching={isLoading} />
                </div>
                <div ref={eventsActivitiesRef}>
                  <EventsActivities isFetching={isLoading} />
                </div>
                <div ref={securitiesMeasuresRef}>
                  <SecuritiesMeasures isFetching={isLoading} />
                </div>
                <div ref={mapRef}>
                  <SchoolProfileMap isFetching={isLoading} />
                </div>
                {renderGoogleReviews()}
              </main>

              <AsideScroll
                props={(
                  <div className={`${styles.asideWrapper} ${styles.sticky}`}>
                    <aside className={styles.aside}>
                      <div
                        className={`${styles.asideContainer} shadow`}
                      >
                        {!!(schoolCanBeContacted || activeExperience) && (
                        <>
                          <div
                            className={bottomBar
                              ? styles.aside__action__btns__mobile
                              : styles.aside__action__btns}
                          >
                            {!!schoolCanBeContacted && !activeExperience && (
                            <div className={styles.rest_actions}>
                              <ExperienceButton hirachy="primary" size="small" onClick={() => handleAction('visit')}>
                                Solicitar Entrevista
                              </ExperienceButton>
                              <ExperienceButton size="small" onClick={() => handleAction('none')}>
                                Enviar Consulta
                              </ExperienceButton>
                            </div>
                            )}
                            {!!schoolCanBeContacted && activeExperience && (
                            <div className={styles.action_stack}>
                              <div className={styles.main_action}>
                                <ExperienceButton hirachy="primary" size="small" onClick={() => handleAction('visit')}>
                                  Solicitar Entrevista
                                </ExperienceButton>
                              </div>
                              <div className={styles.rest_actions}>
                                <ExperienceButton size="small" onClick={() => handleAction('none')}>
                                  Enviar Consulta
                                </ExperienceButton>
                                <AddRemoveCandidateButton
                                  schoolId={schoolData.id}
                                  source={componentFrom?.candidateSource}
                                />
                              </div>
                            </div>
                            )}
                            {!schoolCanBeContacted && !!activeExperience && (
                            <AddRemoveCandidateButton
                              schoolId={schoolData.id}
                              source={componentFrom?.candidateSource}
                            />
                            )}
                          </div>
                          <hr />
                        </>
                        )}
                        {auxSections.map((s) => (
                          <button
                            className={styles.asideBtn}
                            type="button"
                            disabled={s.ref === null}
                            onClick={() => scrollToRef(s.ref)}
                            key={s.id}
                          >
                            <p>{s.name}</p>
                          </button>
                        ))}
                      </div>
                    </aside>
                  </div>
                                )}
              />
            </div>
          </div>
        </div>
        {isContactModalOpen && (
        <PrettyContactModal
          onClose={() => handleCloseContactModal()}
          modalAction={selectedModalAction}
          schoolId={schoolData.id}
          candidateSource={componentFrom?.candidateSource}
        />
        )}
        <CannotPostulateModal
          isOpen={isCannotPostulateModalOpen}
          onRequestClose={(check) => handleCloseCannotPostulateModal(check)}
        />
        <NotLoggedModal Property={notLoggedMsg} Action={notLoggedAction} />
        <Footer />
      </div>
    )
  );
};

const SchoolProfile = ({ id }) => (
  <SchoolProvider id={id}>
    <SchoolProfileContent id={id} />
  </SchoolProvider>
);

export default SchoolProfile;

SchoolProfile.propTypes = {
  id: PropTypes.string.isRequired,
};

SchoolProfileContent.propTypes = {
  id: PropTypes.string.isRequired,
};
